import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Loader2, ChevronLeft, ChevronRight, Eye, User, Building, X, Clock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ESCAVADOR_API_URL, escavadorHeaders } from '../config/api';
import { registrarBilling } from '../services/billing';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const BILLING_URL = process.env.REACT_APP_BILLING_URL || '';

const getAuthHeader = () => {
  try {
    const user = JSON.parse(localStorage.getItem('juridic_user') || '{}');
    return user.token ? `Bearer ${user.token}` : null;
  } catch { return null; }
};

export default function BuscaProcessual() {
  const { darkMode } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');
  const [pageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  // resultados = { tipo: 'cpf_cnpj'|'cnj', envolvido, items, totalItens, totalPaginas, termo }
  const [resultados, setResultados] = useState(null);
  const [toastAlerta, setToastAlerta] = useState(false);
  const [filtroPoloAtivo, setFiltroPoloAtivo] = useState(null); // null = sem filtro, 'ativo', 'passivo', 'semClass'

  const [buscasRecentes, setBuscasRecentes] = useState([]);

  const loadRecentes = async () => {
    try {
      const auth = getAuthHeader();
      if (!auth) return;
      const res = await fetch(`${BILLING_URL}/billing/recentes`, { headers: { 'Authorization': auth } });
      if (!res.ok) return;
      setBuscasRecentes(await res.json());
    } catch {}
  };

  const detectarTipo = (termo) => {
    const digits = termo.replace(/\D/g, '');
    if (digits.length === 11) return 'CPF';
    if (digits.length === 14) return 'CNPJ';
    if (termo.trim().length > 0) return 'CNJ';
    return null;
  };

  const tipoBadge = detectarTipo(termoBusca);

  const getBadgeClasses = (tipo) => {
    if (tipo === 'CPF') return darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
    if (tipo === 'CNPJ') return darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700';
    return darkMode ? 'bg-primary-900/40 text-primary-300' : 'bg-primary-100 text-primary-700';
  };

  useEffect(() => { loadRecentes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restaurar estado ao voltar de DetalhesProcesso
  useEffect(() => {
    const estadoSalvo = sessionStorage.getItem('buscaProcessualState');
    if (estadoSalvo) {
      try {
        const estado = JSON.parse(estadoSalvo);
        setTermoBusca(estado.termoBusca || '');
        setResultados(estado.resultados || null);
        setCurrentPage(estado.currentPage || 1);
        if (estado.filtroPoloAtivo) setFiltroPoloAtivo(estado.filtroPoloAtivo);
        sessionStorage.removeItem('buscaProcessualState');
      } catch (e) {
        sessionStorage.removeItem('buscaProcessualState');
      }
    }
  }, []);


  const handleBuscarEscavador = async (termo, pagina = 1) => {
    try {
      setLoading(true);
      setResultados(null);

      const url = `${ESCAVADOR_API_URL}/envolvido/processos?cpf_cnpj=${encodeURIComponent(termo)}&pagina=${pagina}&limit=50`;
      console.log('🔍 Escavador CPF/CNPJ:', url);

      const response = await fetch(url, { headers: escavadorHeaders() });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }

      const data = await response.json();
      console.log('✅ Resposta Escavador:', data);

      const digits = termo.replace(/\D/g, '');
      registrarBilling({
        route: 'api/v2/envolvido/processos/cpf_cnpj={cpf_cnj}',
        path: `api/v2/envolvido/processos/cpf_cnpj=${termo}`,
        value: 0.50,
        param: digits,
        type: digits.length === 14 ? 'cnpj' : 'cpf',
      });

      const totalItens = data.envolvido_encontrado?.quantidade_processos || data.items?.length || 0;
      const totalPaginas = Math.max(1, Math.ceil(totalItens / pageSize));

      setResultados({
        tipo: 'cpf_cnpj',
        envolvido: data.envolvido_encontrado,
        items: data.items || [],
        totalItens,
        totalPaginas,
        paginaAtual: pagina,
        termo,
      });
      setCurrentPage(pagina);
      if (pagina === 1) loadRecentes();
      if (totalItens > 50) {
        setToastAlerta(true);
        setTimeout(() => setToastAlerta(false), 6000);
      }
    } catch (error) {
      console.error('❌ Erro Escavador:', error);
      alert(`Erro na busca: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarCNJ = async (cnj) => {
    try {
      setLoading(true);
      setResultados(null);

      const url = `${ESCAVADOR_API_URL}/processos/numero_cnj/${encodeURIComponent(cnj)}`;
      console.log('🔍 Escavador CNJ:', url);

      const response = await fetch(url, { headers: escavadorHeaders() });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }

      const data = await response.json();
      console.log('✅ Resposta CNJ:', data);

      registrarBilling({
        route: 'api/v2/processos/numero_cnj/{cnj}',
        path: `api/v2/processos/numero_cnj/${cnj}`,
        value: 0.05,
        param: cnj,
        type: 'cnj',
      });

      const items = Array.isArray(data) ? data : (data.items ? data.items : [data]);

      setResultados({
        tipo: 'cnj',
        envolvido: null,
        items,
        totalItens: items.length,
        totalPaginas: 1,
        paginaAtual: 1,
        termo: cnj,
      });
      setCurrentPage(1);
      loadRecentes();
    } catch (error) {
      console.error('❌ Erro CNJ:', error);
      alert(`Erro na busca: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarUnificado = (e) => {
    if (e) e.preventDefault();
    const termo = termoBusca.trim();
    if (!termo) return;

    const digits = termo.replace(/\D/g, '');
    if (digits.length === 11 || digits.length === 14) {
      handleBuscarEscavador(termo, 1);
    } else {
      handleBuscarCNJ(termo);
    }
  };

  const handleClicarRecente = (recente) => {
    setTermoBusca(recente.param);
    const digits = recente.param.replace(/\D/g, '');
    if (digits.length === 11 || digits.length === 14) {
      handleBuscarEscavador(recente.param, 1);
    } else {
      handleBuscarCNJ(recente.param);
    }
  };

  const handlePageChange = (newPage) => {
    if (resultados?.tipo === 'cpf_cnpj') {
      handleBuscarEscavador(resultados.termo, newPage);
    }
  };

  const handleVerDetalhes = (item) => {
    const estadoAtual = { termoBusca, resultados, currentPage, filtroPoloAtivo };
    sessionStorage.setItem('buscaProcessualState', JSON.stringify(estadoAtual));
    navigate('/detalhes-processo', { state: { processo: item } });
  };

  const getMainFonte = (item) =>
    item.fontes?.find(f => f.tipo === 'TRIBUNAL') || item.fontes?.[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const normalizarNome = (nome) => {
    if (!nome) return '';
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .trim();
  };

  const getPolo = (item) => {
    const nomeEnvolvido = resultados?.envolvido?.nome;

    if (nomeEnvolvido && item.fontes) {
      const nomeNormalizado = normalizarNome(nomeEnvolvido);

      // Procura em TODAS as fontes pela ordem: TRIBUNAL primeiro, depois DIARIO_OFICIAL
      for (const fonte of item.fontes) {
        const envolvido = fonte.envolvidos?.find(env => {
          const nomeEnv = normalizarNome(env.nome);
          return nomeEnv === nomeNormalizado || nomeEnv.includes(nomeNormalizado) || nomeNormalizado.includes(nomeEnv);
        });

        if (envolvido?.polo && envolvido.polo !== 'DESCONHECIDO') {
          return envolvido.polo;
        }
      }
    }

    // Fallback para tipos_envolvido_pesquisado da fonte principal
    const fonte = getMainFonte(item);
    return fonte?.tipos_envolvido_pesquisado?.[0]?.polo || '';
  };

  const filtrarPorPoloAtivo = (items) => {
    if (!filtroPoloAtivo) return items;
    if (filtroPoloAtivo === 'ativo') {
      return items.filter(item => {
        const polo = getPolo(item);
        return polo && polo.toUpperCase() === 'ATIVO';
      });
    }
    if (filtroPoloAtivo === 'passivo') {
      return items.filter(item => {
        const polo = getPolo(item);
        return polo && polo.toUpperCase() === 'PASSIVO';
      });
    }
    if (filtroPoloAtivo === 'semClass') {
      return items.filter(item => {
        const polo = getPolo(item);
        return !polo || (polo.toUpperCase() !== 'ATIVO' && polo.toUpperCase() !== 'PASSIVO');
      });
    }
    return items;
  };

  return (
    <div className="space-y-6">
      {/* Toast limite trial */}
      {toastAlerta && (
        <div className="fixed top-6 right-6 z-50 flex items-start gap-3 max-w-sm w-full bg-amber-50 border border-amber-300 text-amber-900 rounded-xl shadow-lg p-4 animate-fade-in">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">Limite da versão trial atingido</p>
            <p className="text-amber-700 mt-0.5">Esta consulta retornou mais de 50 processos. A versão trial exibe apenas os primeiros 50 CNJs.</p>
          </div>
          <button onClick={() => setToastAlerta(false)} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <FileText className="w-8 h-8" />
          Processos
        </h2>
        <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
          Busque processos por CPF, CNPJ ou número CNJ
        </p>
      </div>

      {/* Formulário de Busca */}
      <div className={`${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
        <form onSubmit={handleBuscarUnificado} className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-neutral-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Digite CPF, CNPJ ou número do processo (CNJ)..."
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                className={`w-full pl-10 ${tipoBadge ? 'pr-20' : 'pr-4'} py-3 rounded-lg border ${
                  darkMode
                    ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
              />
              {tipoBadge && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getBadgeClasses(tipoBadge)}`}>
                    {tipoBadge}
                  </span>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || !termoBusca.trim()}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Buscando...</>
              ) : (
                <><Search className="w-5 h-5" />Buscar</>
              )}
            </button>
          </div>
          <p className={`text-sm ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
            CPF (11 dígitos) e CNPJ (14 dígitos) buscam por envolvidos · Número CNJ busca o processo diretamente
          </p>
        </form>
      </div>

      {/* Buscas Recentes */}
      {buscasRecentes.length > 0 && (
        <div className="space-y-2">
          <div className={`flex items-center gap-1.5 text-xs font-medium ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
            <Clock className="w-3.5 h-3.5" />
            Recentes
          </div>
          <div className="flex flex-wrap gap-2">
            {buscasRecentes.map((recente) => (
              <button
                key={recente.param}
                onClick={() => handleClicarRecente(recente)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                  darkMode
                    ? 'bg-neutral-800 border-neutral-700 hover:border-neutral-500 text-neutral-300'
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${getBadgeClasses(recente.type?.toUpperCase())}`}>
                  {recente.type?.toUpperCase()}
                </span>
                <span className="font-mono text-xs">{recente.param}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resultados */}
      {resultados && (
        <div className="space-y-4">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Resultados
              </h3>
              {resultados.tipo === 'cpf_cnpj' && resultados.envolvido && (
                <div className="flex items-center gap-2 mt-1">
                  {resultados.envolvido.tipo_pessoa === 'JURIDICA'
                    ? <Building className={`w-4 h-4 ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`} />
                    : <User className={`w-4 h-4 ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`} />
                  }
                  <span className={`text-sm font-medium ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                    {resultados.envolvido.nome}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-600'}`}>
                    {resultados.envolvido.tipo_pessoa === 'JURIDICA' ? 'Jurídica' : 'Física'}
                  </span>
                </div>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-100 text-primary-700'
            }`}>
              {resultados.totalItens} {resultados.totalItens === 1 ? 'processo' : 'processos'}
            </span>
          </div>

          {/* Contadores */}
          {resultados.items.length > 0 && (() => {
            const limitado = resultados.totalItens > 50;
            const poloAtivo = resultados.items.filter(item => {
              const polo = getPolo(item);
              return polo && polo.toUpperCase() === 'ATIVO';
            }).length;
            const poloPassivo = resultados.items.filter(item => {
              const polo = getPolo(item);
              return polo && polo.toUpperCase() === 'PASSIVO';
            }).length;
            const semClassificacao = resultados.items.length - poloAtivo - poloPassivo;
            const itensComValor = resultados.items.filter(item => {
              const fonte = getMainFonte(item);
              return !!fonte?.capa?.valor_causa?.valor;
            });
            const somaValor = itensComValor.reduce((acc, item) => {
              const fonte = getMainFonte(item);
              return acc + parseFloat(fonte.capa.valor_causa.valor);
            }, 0);
            const valorFormatado = itensComValor.length > 0
              ? (somaValor / itensComValor.length).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              : '-';

            const stats = [
              { label: 'Total de Processos', value: resultados.items.length, color: limitado ? 'amber' : 'blue', parcial: limitado },
              { label: 'Polo Ativo', value: poloAtivo, color: 'green', parcial: limitado, filterKey: 'ativo', clickable: true },
              { label: 'Polo Passivo', value: poloPassivo, color: 'gray', parcial: limitado, filterKey: 'passivo', clickable: true },
              ...(semClassificacao > 0 ? [{ label: 'Polo Não Definido', value: semClassificacao, color: 'slate', parcial: limitado, filterKey: 'semClass', clickable: true }] : []),
              { label: 'Valor Médio de Causa', value: valorFormatado, color: 'emerald', parcial: limitado },
            ];

            return (
              <div className={`grid gap-3 ${stats.length === 5 ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
                {stats.map(({ label, value, color, parcial, filterKey, clickable }) => {
                  const isActive = filterKey && filtroPoloAtivo === filterKey;
                  return (
                    <div key={label}
                    onClick={() => clickable && setFiltroPoloAtivo(isActive ? null : filterKey)}
                    className={`rounded-xl border p-5 transition-all ${
                      clickable ? 'cursor-pointer' : ''
                    } ${
                      isActive
                        ? darkMode ? 'ring-2 ring-primary-500 bg-primary-900/20 border-primary-600' : 'ring-2 ring-primary-500 bg-primary-50 border-primary-400'
                        : color === 'amber'
                          ? darkMode ? 'bg-amber-900/20 border-amber-800/50' : 'bg-amber-50 border-amber-200'
                          : darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'
                    } ${
                      clickable && !isActive ? darkMode ? 'hover:border-neutral-700' : 'hover:border-gray-300' : ''
                    }`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-xs font-medium ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{label}</p>
                      {parcial && <span className={`text-xs ${darkMode ? 'text-neutral-600' : 'text-gray-400'}`}>parcial</span>}
                    </div>
                    <p className={`text-2xl font-bold whitespace-nowrap ${
                      color === 'amber' ? darkMode ? 'text-amber-400' : 'text-amber-600'
                      : color === 'green' ? darkMode ? 'text-green-400' : 'text-green-600'
                      : color === 'gray' ? darkMode ? 'text-neutral-400' : 'text-gray-500'
                      : color === 'slate' ? darkMode ? 'text-slate-400' : 'text-slate-600'
                      : color === 'emerald' ? darkMode ? 'text-emerald-400' : 'text-emerald-600'
                      : darkMode ? 'text-white' : 'text-gray-900'
                    }`}>{value}</p>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Gráfico por mês/ano */}
          {resultados.items.length > 0 && (() => {
            const contagem = {};
            resultados.items.forEach(item => {
              if (!item.data_inicio) return;
              const [ano, mes] = item.data_inicio.split('-');
              const chave = `${mes}/${ano}`;
              contagem[chave] = (contagem[chave] || 0) + 1;
            });
            const dados = Object.entries(contagem)
              .sort(([a], [b]) => {
                const [ma, aa] = a.split('/');
                const [mb, ab] = b.split('/');
                return new Date(`${aa}-${ma}`) - new Date(`${ab}-${mb}`);
              })
              .map(([mes, total]) => ({ mes, total }));

            if (dados.length < 1) return null;

            return (
              <div className={`rounded-xl border p-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
                <p className={`text-xs font-medium mb-4 ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>Processos por data de distribuição</p>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={dados} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradProcessos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={darkMode ? '#be123c' : '#cc092f'} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={darkMode ? '#be123c' : '#cc092f'} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#262626' : '#f0f0f0'} vertical={false} />
                    <XAxis dataKey="mes" tick={{ fontSize: 11, fill: darkMode ? '#737373' : '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: darkMode ? '#737373' : '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: darkMode ? '#171717' : '#fff', border: `1px solid ${darkMode ? '#404040' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: darkMode ? '#d4d4d4' : '#374151', fontWeight: 600 }}
                      itemStyle={{ color: darkMode ? '#f87171' : '#cc092f' }}
                      formatter={(v) => [v, 'Processos']}
                    />
                    <Area type="monotone" dataKey="total" stroke={darkMode ? '#be123c' : '#cc092f'} strokeWidth={2} fill="url(#gradProcessos)" dot={{ r: 3, fill: darkMode ? '#be123c' : '#cc092f', strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            );
          })()}

          {/* Grid de cards */}
          {(() => {
            const itemsFiltrados = filtrarPorPoloAtivo(resultados.items);
            return itemsFiltrados.length === 0 ? (
              <div className={`text-center py-16 rounded-xl border ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
                <FileText className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-neutral-600' : 'text-gray-300'}`} />
                <p className={`text-lg font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                  {filtroPoloAtivo ? 'Nenhum processo nesta categoria' : 'Nenhum processo encontrado'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {itemsFiltrados.map((item, idx) => {
                const fonte = getMainFonte(item);
                const capa = fonte?.capa;
                const isArquivado = item.fontes_tribunais_estao_arquivadas;
                const tribunal = item.unidade_origem?.tribunal_sigla || fonte?.sigla || '-';
                const uf = item.estado_origem?.sigla || '-';
                const cidade = item.unidade_origem?.cidade || '-';
                const orgao = item.unidade_origem?.nome || fonte?.capa?.orgao_julgador || '-';

                return (
                  <div
                    key={idx}
                    className={`flex flex-col rounded-xl border p-5 transition-shadow hover:shadow-md ${
                      darkMode ? 'bg-neutral-900 border-neutral-800 hover:border-neutral-700' : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Cabeçalho do card: CNJ + badges + botão */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h4 className={`text-sm font-bold font-mono leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.numero_cnj}
                      </h4>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {capa?.area && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {capa.area}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          isArquivado
                            ? darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                            : darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                        }`}>
                          {isArquivado ? 'Arquivado' : 'Ativo'}
                        </span>
                      </div>
                    </div>

                    {/* Polos */}
                    {(item.titulo_polo_ativo || item.titulo_polo_passivo) && (
                      <div className={`space-y-1 mb-3 pb-3 border-b ${darkMode ? 'border-neutral-800' : 'border-gray-100'}`}>
                        {item.titulo_polo_ativo && (
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${darkMode ? 'bg-primary-500' : 'bg-primary-600'}`} />
                            <span className={darkMode ? 'text-neutral-500' : 'text-gray-400'}>Ativo</span>
                            <span className={`font-medium truncate ${darkMode ? 'text-neutral-200' : 'text-gray-800'}`}>{item.titulo_polo_ativo}</span>
                          </div>
                        )}
                        {item.titulo_polo_passivo && (
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${darkMode ? 'bg-purple-500' : 'bg-purple-600'}`} />
                            <span className={darkMode ? 'text-neutral-500' : 'text-gray-400'}>Passivo</span>
                            <span className={`font-medium truncate ${darkMode ? 'text-neutral-200' : 'text-gray-800'}`}>{item.titulo_polo_passivo}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tribunal / UF / Cidade */}
                    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 mb-2 text-sm`}>
                      {tribunal !== '-' && (
                        <span className={`font-semibold ${darkMode ? 'text-primary-400' : 'text-primary-600'}`}>{tribunal}</span>
                      )}
                      {uf !== '-' && <span className={darkMode ? 'text-neutral-500' : 'text-gray-400'}>·</span>}
                      {uf !== '-' && <span className={darkMode ? 'text-neutral-400' : 'text-gray-500'}>{uf}</span>}
                      {cidade !== '-' && <span className={darkMode ? 'text-neutral-500' : 'text-gray-400'}>·</span>}
                      {cidade !== '-' && <span className={darkMode ? 'text-neutral-400' : 'text-gray-500'}>{cidade}</span>}
                    </div>

                    {/* Órgão */}
                    {orgao !== '-' && orgao !== item.unidade_origem?.tribunal_sigla && (
                      <p className={`text-xs truncate mb-2 ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>{orgao}</p>
                    )}

                    {/* Classe / Assunto */}
                    {capa && (capa.classe || capa.assunto) && (
                      <div className={`flex flex-wrap gap-x-2 gap-y-0.5 mb-2 text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                        {capa.classe && <span>{capa.classe}</span>}
                        {capa.classe && capa.assunto && <span className={darkMode ? 'text-neutral-600' : 'text-gray-300'}>·</span>}
                        {capa.assunto && <span>{capa.assunto}</span>}
                      </div>
                    )}

                    {/* Spacer para empurrar rodapé */}
                    <div className="flex-1" />

                    {/* Rodapé do card: valor + datas + botão */}
                    <div className={`flex items-end justify-between gap-2 mt-3 pt-3 border-t ${darkMode ? 'border-neutral-800' : 'border-gray-100'}`}>
                      <div className={`flex flex-wrap gap-x-3 gap-y-0.5 text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                        {capa?.valor_causa?.valor_formatado && (
                          <span className={`font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {capa.valor_causa.valor_formatado}
                          </span>
                        )}
                        {item.data_inicio && <span>Início: {formatDate(item.data_inicio)}</span>}
                        {item.quantidade_movimentacoes > 0 && <span>{item.quantidade_movimentacoes} movs.</span>}
                      </div>
                      <button
                        onClick={() => handleVerDetalhes(item)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                          darkMode
                            ? 'bg-primary-900/30 text-primary-400 hover:bg-primary-900/50'
                            : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                        }`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Ver detalhes
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}

          {/* Paginação */}
          {resultados.tipo === 'cpf_cnpj' && resultados.totalPaginas > 1 && resultados.totalItens <= 50 && (
            <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
              darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'
            }`}>
              <div className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                Página {resultados.paginaAtual} de {resultados.totalPaginas} · {resultados.totalItens} processos
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(resultados.paginaAtual - 1)}
                  disabled={resultados.paginaAtual === 1 || loading}
                  className={`flex items-center gap-1 px-3 py-2 rounded border transition-all text-sm ${
                    resultados.paginaAtual === 1 || loading
                      ? darkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-600 cursor-not-allowed' : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode ? 'bg-neutral-700 border-neutral-600 text-neutral-300 hover:bg-neutral-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" /> Anterior
                </button>
                <button
                  onClick={() => handlePageChange(resultados.paginaAtual + 1)}
                  disabled={resultados.paginaAtual === resultados.totalPaginas || loading}
                  className={`flex items-center gap-1 px-3 py-2 rounded border transition-all text-sm ${
                    resultados.paginaAtual === resultados.totalPaginas || loading
                      ? darkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-600 cursor-not-allowed' : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode ? 'bg-neutral-700 border-neutral-600 text-neutral-300 hover:bg-neutral-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Próxima <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
